/**
 * Plain zip: what an ordinary backup is (a ledger and its files), opened by any
 * archiver. Entries are deflated, with their CRC-32 — the format every tool
 * reads. The password-protected kind is `securezip.js`, the same entries under
 * AES; `unzip` reads this kind, and says `zip.locked` for that one.
 */
// Explicit extension: read from plain Node too (see securezip.js).
import { looksLikeZip } from './securezip.js'

const DEFLATED = 8
const STORED = 0
const AES_METHOD = 99

const utf8 = (text) => new TextEncoder().encode(text)
const u16 = (b, o) => b[o] | (b[o + 1] << 8)
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0

let table = null
/** CRC-32, as zip wants it. */
export function crc32(bytes) {
  if (!table) {
    table = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      table[n] = c >>> 0
    }
  }
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

async function through(bytes, stream) {
  const out = new Response(new Blob([bytes]).stream().pipeThrough(stream))
  return new Uint8Array(await out.arrayBuffer())
}
const deflate = (bytes) => through(bytes, new CompressionStream('deflate-raw'))
const inflate = (bytes) => through(bytes, new DecompressionStream('deflate-raw'))

function concat(parts) {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0))
  let at = 0
  for (const p of parts) {
    out.set(p, at)
    at += p.length
  }
  return out
}

/**
 * @param {{name: string, bytes: Uint8Array}[]} files
 * @returns {Promise<Uint8Array>} the archive
 */
export async function zip(files) {
  const local = []
  const central = []
  let offset = 0
  for (const file of files) {
    const name = utf8(file.name)
    const crc = crc32(file.bytes)
    const packed = await deflate(file.bytes)
    const header = new Uint8Array(30)
    const hv = new DataView(header.buffer)
    hv.setUint32(0, 0x04034b50, true)
    hv.setUint16(4, 20, true)
    hv.setUint16(6, 0x0800, true) // names in UTF-8
    hv.setUint16(8, DEFLATED, true)
    hv.setUint32(14, crc, true)
    hv.setUint32(18, packed.length, true)
    hv.setUint32(22, file.bytes.length, true)
    hv.setUint16(26, name.length, true)
    const entryAt = offset
    local.push(header, name, packed)
    offset += 30 + name.length + packed.length

    const record = new Uint8Array(46)
    const rv = new DataView(record.buffer)
    rv.setUint32(0, 0x02014b50, true)
    rv.setUint16(4, 20, true)
    rv.setUint16(6, 20, true)
    rv.setUint16(8, 0x0800, true)
    rv.setUint16(10, DEFLATED, true)
    rv.setUint32(16, crc, true)
    rv.setUint32(20, packed.length, true)
    rv.setUint32(24, file.bytes.length, true)
    rv.setUint16(28, name.length, true)
    rv.setUint32(42, entryAt, true)
    central.push(record, name)
  }
  const directory = central.reduce((n, p) => n + p.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, files.length, true)
  ev.setUint16(10, files.length, true)
  ev.setUint32(12, directory, true)
  ev.setUint32(16, offset, true)
  return concat([...local, ...central, end])
}

/** Whether a zip's entries are password-protected (`securezip.js`'s kind). */
export function isLocked(archive) {
  const bytes = archive instanceof Uint8Array ? archive : new Uint8Array(archive)
  return looksLikeZip(bytes) && u16(bytes, 8) === AES_METHOD
}

/**
 * Reads a plain zip — deflated or stored entries, checked against their
 * CRC-32. Throws `zip.locked` for a password-protected one, `zip.damaged`.
 * @returns {Promise<{name: string, bytes: Uint8Array}[]>}
 */
export async function unzip(archive) {
  const bytes = archive instanceof Uint8Array ? archive : new Uint8Array(archive)
  if (!looksLikeZip(bytes)) throw Object.assign(new Error('That is not a zip file'), { code: 'zip.not-zip' })
  const files = []
  let at = 0
  while (at + 30 <= bytes.length && u32(bytes, at) === 0x04034b50) {
    const method = u16(bytes, at + 8)
    const crc = u32(bytes, at + 14)
    const packedLength = u32(bytes, at + 18)
    const nameLength = u16(bytes, at + 26)
    const extraLength = u16(bytes, at + 28)
    const name = new TextDecoder().decode(bytes.subarray(at + 30, at + 30 + nameLength))
    const dataAt = at + 30 + nameLength + extraLength
    const packed = bytes.subarray(dataAt, dataAt + packedLength)
    if (method === AES_METHOD) throw Object.assign(new Error('That backup is protected by a password'), { code: 'zip.locked' })
    if (method !== DEFLATED && method !== STORED) {
      throw Object.assign(new Error('That zip is packed in a way OKANE cannot read'), { code: 'zip.not-ours' })
    }
    const data = method === STORED ? packed.slice() : await inflate(packed)
    if (crc32(data) !== crc) throw Object.assign(new Error('That backup has been damaged since it was made'), { code: 'zip.damaged' })
    files.push({ name, bytes: data })
    at = dataAt + packedLength
  }
  if (!files.length) throw Object.assign(new Error('There is nothing in that zip'), { code: 'zip.empty' })
  return files
}
